// Inventory & Assets — Super Admin: Category & Subcategory manager.
// Add / rename / merge / activate-deactivate subcategories.

import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "../../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plus, Pencil, Merge, Power, PowerOff, Tags }
  from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, token, body = {}) => {
  const r = await fetch(`${API}/api/inventory${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

export function CategoryManagerPanel({ token, isSuperAdmin }) {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [action, setAction] = useState(null);  // {type: 'rename'|'merge', name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await post("/categories/list", token);
      setCats(d.categories || []);
      if (!active && d.categories?.length) setActive(d.categories[0].code);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, active]);
  useEffect(() => { load(); }, [load]);

  const cat = cats.find(c => c.code === active);

  const toggle = async (name) => {
    if (!window.confirm(`${name} — toggle active/deactivated?`)) return;
    try {
      const r = await post("/subcategories/toggle-active", token,
        { category_code: active, name });
      toast.success(`${name} ${r.status}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!isSuperAdmin) return (
    <Card><CardContent className="p-6 text-center text-muted-foreground">
      This panel is available to Super Admin only.
    </CardContent></Card>
  );

  return (
    <Card data-testid="inv-catman-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Tags className="w-4 h-4" /> Categories & Subcategories (Super Admin)
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Manage the master subcategory list. Deactivating a subcategory hides it from
            new asset entries but preserves it on historical records.
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={active} onValueChange={setActive}>
              <SelectTrigger className="w-72" data-testid="inv-catman-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map(c => <SelectItem key={c.code} value={c.code}>{c.letter}. {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="inv-catman-add">
            <Plus className="w-4 h-4 mr-1" /> Add Subcategory
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!cat ? <div className="text-muted-foreground p-4">Loading…</div> : (
          <>
            <div className="text-xs text-muted-foreground mb-2">
              {cat.subcategories?.length || 0} active • {cat.deactivated_subcategories?.length || 0} deactivated
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Subcategory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(cat.subcategories || []).map(s => (
                  <TableRow key={`a-${s}`} data-testid={`inv-catman-row-${s}`}>
                    <TableCell>{s}</TableCell>
                    <TableCell><Badge variant="default">Active</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline"
                        onClick={() => setAction({ type: "rename", name: s })}
                        data-testid={`inv-catman-rename-${s}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => setAction({ type: "merge", name: s })}
                        data-testid={`inv-catman-merge-${s}`}>
                        <Merge className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggle(s)}
                        data-testid={`inv-catman-deactivate-${s}`}>
                        <PowerOff className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(cat.deactivated_subcategories || []).map(s => (
                  <TableRow key={`d-${s}`} className="bg-muted/30">
                    <TableCell className="line-through text-muted-foreground">{s}</TableCell>
                    <TableCell><Badge variant="outline">Deactivated</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => toggle(s)}
                        data-testid={`inv-catman-reactivate-${s}`}>
                        <Power className="w-4 h-4 mr-1" /> Re-activate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>

      {showAdd && <AddDialog token={token} category={cat}
        onClose={() => { setShowAdd(false); load(); }} />}
      {action && <ActionDialog token={token} category={cat} action={action}
        onClose={() => { setAction(null); load(); }} />}
    </Card>
  );
}

function AddDialog({ token, category, onClose }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await post("/subcategories/add", token, { category_code: category.code, name });
      toast.success(`"${name}" added to ${category.letter}. ${category.name}`);
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-catman-add-dialog">
        <DialogHeader><DialogTitle>Add Subcategory → {category?.letter}. {category?.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Subcategory Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
            data-testid="inv-catman-add-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-catman-add-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ token, category, action, onClose }) {
  const isRename = action.type === "rename";
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!target.trim()) return toast.error("Target required");
    setBusy(true);
    try {
      if (isRename) {
        const r = await post("/subcategories/rename", token,
          { category_code: category.code, name: action.name, new_name: target });
        toast.success(`Renamed — ${r.renamed_assets} asset(s) updated`);
      } else {
        const r = await post("/subcategories/merge", token,
          { category_code: category.code, name: action.name, target_name: target });
        toast.success(`Merged — ${r.merged_assets} asset(s) moved to "${target}"`);
      }
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`inv-catman-${action.type}-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {isRename ? "Rename" : "Merge"} — {action.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {isRename ? (
            <>
              <Label>New Name</Label>
              <Input value={target} onChange={(e) => setTarget(e.target.value)} autoFocus
                placeholder="New subcategory name"
                data-testid="inv-catman-rename-target" />
            </>
          ) : (
            <>
              <Label>Merge into</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="inv-catman-merge-target"><SelectValue placeholder="Pick a target subcategory" /></SelectTrigger>
                <SelectContent>
                  {(category?.subcategories || []).filter(s => s !== action.name).map(s =>
                    <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">
                All assets currently pointing at &quot;{action.name}&quot; will be
                re-labelled to the target subcategory.
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-catman-action-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isRename ? "Rename" : "Merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
